import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { AirPath, AirPathDirection } from "@domain/project/floorplan/air-path";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterCabinet } from "@domain/project/floorplan/floorplan-gas-warning-center-cabinet";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanPlaceholderType, FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanState } from "@domain/project/floorplan/floorplan-state";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";

import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone, DustExZoneType } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone, ExZoneType } from "@domain/project/floorplan/zones/ex-zone";
import { WildcardZone, WildcardZoneType } from "@domain/project/floorplan/zones/wildcard-zone";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { IsArrayOfInstancesOf, IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import { IsArray, IsBoolean, IsDataURI, IsOptional, IsString, IsUUID, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";
import { KvmConfiguration } from "../configurations/monitoring/kvm-configuration";

export type ProductTypesInGroup =
  | FloorplanAlarmDevice
  | FloorplanGasWarningCenter
  | FloorplanGasWarningCenterPlaceholder
  | FloorplanSimplePlaceholder
  | FloorplanPlasticSign
  | FloorplanSignalElement
  | FloorplanTransmitter
  | FloorplanTransmitterPlaceholder
  | FloorplanFlameDetector
  | FloorplanFlameDetectorPlaceholder
  | FloorplanPatientMonitor
  | FloorplanCentralStation
  | FloorplanKvm
  | FloorplanDisplay
  | FloorplanTelemetry
  | FloorplanUtility;

export class Floorplan {
  private ensureArraysInitialized() {
    // FGDS
    (this as any)._alarmDevices = this._alarmDevices ?? [];
    (this as any)._alarmDevicePlaceholders = this._alarmDevicePlaceholders ?? [];
    (this as any)._transmitters = this._transmitters ?? [];
    (this as any)._transmitterPlaceholders = this._transmitterPlaceholders ?? [];
    (this as any)._flameDetectors = this._flameDetectors ?? [];
    (this as any)._flameDetectorPlaceholders = this._flameDetectorPlaceholders ?? [];
    (this as any)._gasWarningCenters = this._gasWarningCenters ?? [];
    (this as any)._gasWarningCenterPlaceholders = this._gasWarningCenterPlaceholders ?? [];
    (this as any)._signalElements = this._signalElements ?? [];
    (this as any)._signalElementPlaceholders = this._signalElementPlaceholders ?? [];
    (this as any)._plasticSigns = this._plasticSigns ?? [];
    (this as any)._plasticSignPlaceholders = this._plasticSignPlaceholders ?? [];
    (this as any)._exZones = this._exZones ?? [];
    (this as any)._dustExZones = this._dustExZones ?? [];
    (this as any)._wildcardZones = this._wildcardZones ?? [];
    (this as any)._dangerAreas = this._dangerAreas ?? [];
    (this as any)._airPaths = this._airPaths ?? [];
    (this as any)._pipelines = this._pipelines ?? [];
    (this as any)._cableRoutes = this._cableRoutes ?? [];
    (this as any)._protectiveTubes = this._protectiveTubes ?? [];
    (this as any)._wires = this._wires ?? [];
    // Monitoring
    (this as any)._patientMonitors = this._patientMonitors ?? [];
    (this as any)._patientMonitorPlaceholders = this._patientMonitorPlaceholders ?? [];
    (this as any)._centralStations = this._centralStations ?? [];
    (this as any)._centralStationPlaceholders = this._centralStationPlaceholders ?? [];
    (this as any)._telemetries = this._telemetries ?? [];
    (this as any)._telemetryPlaceholders = this._telemetryPlaceholders ?? [];
    (this as any)._kvms = this._kvms ?? [];
    (this as any)._kvmPlaceholders = this._kvmPlaceholders ?? [];
    (this as any)._displays = this._displays ?? [];
    (this as any)._displayPlaceholders = this._displayPlaceholders ?? [];
    (this as any)._utilities = this._utilities ?? [];
    (this as any)._utilityPlaceholders = this._utilityPlaceholders ?? [];
    (this as any)._isolationRooms = this._isolationRooms ?? [];
    (this as any)._itRooms = this._itRooms ?? [];
    (this as any)._operatingTheatres = this._operatingTheatres ?? [];
    (this as any)._procedureRooms = this._procedureRooms ?? [];
    (this as any)._standardRooms = this._standardRooms ?? [];
    // Other
    (this as any)._images = this._images ?? [];
    (this as any)._floorplanTexts = this._floorplanTexts ?? [];
    (this as any)._floorplanFreeTexts = this._floorplanFreeTexts ?? [];
    (this as any)._measurementLines = this._measurementLines ?? [];
  }
  @IsUUID()
  public readonly id: string;

  @IsDataURI()
  @IsOptional()
  public fileUrl?: string;

  @IsBoolean()
  public selected: boolean = false;

  @Exclude()
  private _project?: ScopedProject;

  @Exclude()
  private _onUpdate?: (floorplan: Floorplan, type: FloorplanEventType, subject: any) => void;

  @IsString()
  @Expose({ name: "name" })
  private _name: string;

  @IsOptional()
  @IsString()
  @Expose({ name: "description" })
  private _description?: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  @Expose({ name: "tags" })
  private _tags?: string[];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanImage)
  @Type(() => FloorplanImage)
  @Expose({ name: "images" })
  private readonly _images: FloorplanImage[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanAlarmDevice)
  @Type(() => FloorplanAlarmDevice)
  @Expose({ name: "alarmDevices" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _alarmDevices: FloorplanAlarmDevice[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "alarmDevicePlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _alarmDevicePlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanTransmitter)
  @Type(() => FloorplanTransmitter)
  @Expose({ name: "transmitters" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _transmitters: FloorplanTransmitter[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanTransmitterPlaceholder)
  @Type(() => FloorplanTransmitterPlaceholder)
  @Expose({ name: "transmitterPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _transmitterPlaceholders: FloorplanTransmitterPlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanFlameDetector)
  @Type(() => FloorplanFlameDetector)
  @Expose({ name: "flameDetectors" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _flameDetectors: FloorplanFlameDetector[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanFlameDetectorPlaceholder)
  @Type(() => FloorplanFlameDetectorPlaceholder)
  @Expose({ name: "flameDetectorPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _flameDetectorPlaceholders: FloorplanFlameDetectorPlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanGasWarningCenter)
  @Type(() => FloorplanGasWarningCenter)
  @Expose({ name: "gasWarningCenters" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _gasWarningCenters: FloorplanGasWarningCenter[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanGasWarningCenterPlaceholder)
  @Type(() => FloorplanGasWarningCenterPlaceholder)
  @Expose({ name: "gasWarningCenterPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _gasWarningCenterPlaceholders: FloorplanGasWarningCenterPlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSignalElement)
  @Type(() => FloorplanSignalElement)
  @Expose({ name: "signalElements" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _signalElements: FloorplanSignalElement[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "signalElementPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _signalElementPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanPlasticSign)
  @Type(() => FloorplanPlasticSign)
  @Expose({ name: "plasticSigns" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _plasticSigns: FloorplanPlasticSign[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "plasticSignPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _plasticSignPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanText)
  @Type(() => FloorplanText)
  @Expose({ name: "floorplanTexts" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _floorplanTexts: FloorplanText[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanFreeText)
  @Type(() => FloorplanFreeText)
  @Expose({ name: "floorplanFreeTexts" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _floorplanFreeTexts: FloorplanFreeText[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(ExZone)
  @Type(() => ExZone)
  @Expose({ name: "exZones" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _exZones: ExZone[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(DustExZone)
  @Type(() => DustExZone)
  @Expose({ name: "dustExZones" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _dustExZones: DustExZone[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(WildcardZone)
  @Type(() => WildcardZone)
  @Expose({ name: "wildcardZones" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _wildcardZones: WildcardZone[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(DangerArea)
  @Type(() => DangerArea)
  @Expose({ name: "dangerAreas" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _dangerAreas: DangerArea[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(MeasurementLine)
  @Type(() => MeasurementLine)
  @Expose({ name: "measurementLines" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _measurementLines: MeasurementLine[] = [];

  @ValidateNested()
  @Type(() => ReferenceMeasure)
  @Expose({ name: "referenceMeasure" })
  @Transform(({ value }) => value ?? undefined, { toClassOnly: true })
  private _referenceMeasure: ReferenceMeasure | undefined;

  @ValidateNested()
  @IsArrayOfInstancesOf(AirPath)
  @Type(() => AirPath)
  @Expose({ name: "airPaths" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _airPaths: AirPath[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(Pipeline)
  @Type(() => Pipeline)
  @Expose({ name: "pipelines" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _pipelines: Pipeline[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(CableRoute)
  @Type(() => CableRoute)
  @Expose({ name: "cableRoutes" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _cableRoutes: CableRoute[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(ProtectiveTube)
  @Type(() => ProtectiveTube)
  @Expose({ name: "protectiveTubes" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _protectiveTubes: ProtectiveTube[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(Wire)
  @Type(() => Wire)
  @Expose({ name: "wires" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _wires: Wire[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanPatientMonitor)
  @Type(() => FloorplanPatientMonitor)
  @Expose({ name: "patientMonitors" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _patientMonitors: FloorplanPatientMonitor[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "patientMonitorPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _patientMonitorPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanCentralStation)
  @Type(() => FloorplanCentralStation)
  @Expose({ name: "centralStations" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _centralStations: FloorplanCentralStation[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "centralStationPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _centralStationPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanTelemetry)
  @Type(() => FloorplanTelemetry)
  @Expose({ name: "telemetries" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _telemetries: FloorplanTelemetry[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanKvm)
  @Type(() => FloorplanKvm)
  @Expose({ name: "kvm" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _kvms: FloorplanKvm[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "telemetryPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _telemetryPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "kvmPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _kvmPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanDisplay)
  @Type(() => FloorplanDisplay)
  @Expose({ name: "displays" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _displays: FloorplanDisplay[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "displayPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _displayPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanUtility)
  @Type(() => FloorplanUtility)
  @Expose({ name: "utilities" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _utilities: FloorplanUtility[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanSimplePlaceholder)
  @Type(() => FloorplanSimplePlaceholder)
  @Expose({ name: "utilityPlaceholders" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _utilityPlaceholders: FloorplanSimplePlaceholder[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(IsolationRoom)
  @Type(() => IsolationRoom)
  @Expose({ name: "isolationRooms" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _isolationRooms: IsolationRoom[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(ItRoom)
  @Type(() => ItRoom)
  @Expose({ name: "itRooms" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _itRooms: ItRoom[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(OperatingTheatre)
  @Type(() => OperatingTheatre)
  @Expose({ name: "operatingTheatres" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _operatingTheatres: OperatingTheatre[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(ProcedureRoom)
  @Type(() => ProcedureRoom)
  @Expose({ name: "procedureRooms" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _procedureRooms: ProcedureRoom[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(StandardRoom)
  @Type(() => StandardRoom)
  @Expose({ name: "standardRooms" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private readonly _standardRooms: StandardRoom[] = [];

  @ValidateNested()
  @IsInstanceOf(FloorplanState)
  @Type(() => FloorplanState)
  @Transform(({ value }) => value ?? new FloorplanState(), { toClassOnly: true })
  @Expose({ name: "floorplanState" })
  private _floorplanState: FloorplanState = new FloorplanState();

  constructor(
    project: ScopedProject,
    id: string,
    name: string,
    fileUrl?: string,
    selected: boolean = false,
    description?: string,
    tags?: string[],
  ) {
    this._project = project;
    this._name = name;
    this.id = id;
    this.fileUrl = fileUrl;
    this.selected = selected;
    this._description = description;
    this._tags = tags;
    this.ensureArraysInitialized();
  }

  init(project: ScopedProject, onUpdate?: (floorplan: Floorplan, type: FloorplanEventType, subject: any) => void) {
    this._project = project;
    this._onUpdate = onUpdate;
    this.ensureArraysInitialized();
    this._images.forEach((image) => image.init(this));
    this._floorplanTexts.forEach((floorplanText) => floorplanText.init(this));
    this._floorplanFreeTexts.forEach((freeText) => freeText.init(this));
    this._measurementLines.forEach((measurementLine) => measurementLine.init(this));

    if (isFgdsProject(project)) {
      this.initFgdsItems();
    }

    if (isMonitoringProject(project)) {
      this.initMonitoringItems();
    }

    if (this._referenceMeasure) {
      this._referenceMeasure.init(this);
    }
    this._floorplanState.init(this);
  }

  private initFgdsItems() {
    this._alarmDevices.forEach((alarmDevice) => alarmDevice.init(this));
    this._alarmDevicePlaceholders.forEach((placeholder) => placeholder.init(this));
    this._transmitters.forEach((transmitter) => transmitter.init(this));
    this._transmitterPlaceholders.forEach((placeholder) => placeholder.init(this));
    this._flameDetectors.forEach((flameDetector) => flameDetector.init(this));
    this._flameDetectorPlaceholders.forEach((placeholder) => placeholder.init(this));
    this._gasWarningCenters.forEach((gasWarningCenter) => gasWarningCenter.init(this));
    this._gasWarningCenterPlaceholders.forEach((placeholder) => placeholder.init(this));
    this._signalElements.forEach((signalElement) => signalElement.init(this));
    this._signalElementPlaceholders.forEach((placeholder) => placeholder.init(this));
    this._plasticSigns.forEach((plasticSign) => plasticSign.init(this));
    this._plasticSignPlaceholders.forEach((placeholder) => placeholder.init(this));
    this._exZones.forEach((exZone) => exZone.init(this));
    this._dustExZones.forEach((dustExZone) => dustExZone.init(this));
    this._wildcardZones.forEach((wildcardZone) => wildcardZone.init(this));
    this._dangerAreas.forEach((dangerArea) => dangerArea.init(this));
    this._airPaths.forEach((airPath) => airPath.init(this));
    this._pipelines.forEach((pipeline) => pipeline.init(this));
    this._cableRoutes.forEach((cableRoute) => cableRoute.init(this));
    this._protectiveTubes.forEach((protectiveTube) => protectiveTube.init(this));
    this._wires.forEach((wire) => wire.init(this));

    this.removeOrphanedProductItems();
  }

  private initMonitoringItems() {
    this._patientMonitors.forEach((patientMonitor) => patientMonitor.init(this));
    this._patientMonitorPlaceholders.forEach((patientMonitorPlaceholder) => patientMonitorPlaceholder.init(this));
    this._centralStations.forEach((centralStation) => centralStation.init(this));
    this._centralStationPlaceholders.forEach((centralStationPlaceholder) => centralStationPlaceholder.init(this));
    this._telemetries.forEach((telemetry) => telemetry.init(this));
    this._telemetryPlaceholders.forEach((telemetryPlaceholder) => telemetryPlaceholder.init(this));
    this._kvms.forEach((kvm) => kvm.init(this));
    this._kvmPlaceholders.forEach((kvmPlaceholder) => kvmPlaceholder.init(this));
    this._displays.forEach((display) => display.init(this));
    this._displayPlaceholders.forEach((displayPlaceholder) => displayPlaceholder.init(this));
    this._utilities.forEach((utility) => utility.init(this));
    this._utilityPlaceholders.forEach((utilityPlaceholder) => utilityPlaceholder.init(this));
    this._isolationRooms.forEach((isolationRoom) => isolationRoom.init(this));
    this._itRooms.forEach((itRoom) => itRoom.init(this));
    this._operatingTheatres.forEach((operatingTheatre) => operatingTheatre.init(this));
    this._procedureRooms.forEach((procedureRoom) => procedureRoom.init(this));
    this._standardRooms.forEach((standardRoom) => standardRoom.init(this));

    this.removeOrphanedProductItems();
  }

  private removeOrphanedProductItems() {
    const removeOrphansInPlace = <T extends ProductConfiguration>(items: FloorplanProductItem<T>[]): void => {
      for (let i = items.length - 1; i >= 0; i--) {
        if (!items[i].config) {
          this.removeItemReferenceFromAllGroups(items[i].id);
          items.splice(i, 1);
        }
      }
    };

    const countBefore =
      this._alarmDevices.length +
      this._transmitters.length +
      this._gasWarningCenters.length +
      this._signalElements.length +
      this._plasticSigns.length +
      this._flameDetectors.length +
      this._patientMonitors.length +
      this._centralStations.length +
      this._telemetries.length +
      this._kvms.length +
      this._displays.length +
      this._utilities.length;

    removeOrphansInPlace(this._alarmDevices);
    removeOrphansInPlace(this._transmitters);
    removeOrphansInPlace(this._gasWarningCenters);
    removeOrphansInPlace(this._signalElements);
    removeOrphansInPlace(this._plasticSigns);
    removeOrphansInPlace(this._flameDetectors);
    removeOrphansInPlace(this._patientMonitors);
    removeOrphansInPlace(this._centralStations);
    removeOrphansInPlace(this._telemetries);
    removeOrphansInPlace(this._kvms);
    removeOrphansInPlace(this._displays);
    removeOrphansInPlace(this._utilities);

    const countAfter =
      this._alarmDevices.length +
      this._transmitters.length +
      this._gasWarningCenters.length +
      this._signalElements.length +
      this._plasticSigns.length +
      this._flameDetectors.length +
      this._patientMonitors.length +
      this._centralStations.length +
      this._telemetries.length +
      this._kvms.length +
      this._displays.length +
      this._utilities.length;

    const removed = countBefore - countAfter;
    if (removed > 0) {
      console.warn(`Removed ${removed} orphaned floorplan product item(s) from floorplan '${this._name}'.`);
    }
  }

  static create(project: ScopedProject, name: string, fileUrl?: string, description?: string, tags?: string[]): Floorplan {
    return new Floorplan(project, uuidv4(), name, fileUrl, false, description, tags);
  }

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this._name = value;
    this.project.publishUpdate(ProjectEventType.FLOORPLAN_UPDATED, this);
  }

  get description(): string | undefined {
    return this._description;
  }

  set description(value: string | undefined) {
    this._description = value;
    this.project.publishUpdate(ProjectEventType.FLOORPLAN_UPDATED, this);
  }

  get tags(): string[] | undefined {
    return this._tags;
  }

  set tags(value: string[] | undefined) {
    this._tags = value;
    this.project.publishUpdate(ProjectEventType.FLOORPLAN_UPDATED, this);
  }

  get project(): ScopedProject {
    if (!this._project) {
      throw Error("Project must be set before accessing the floorplan");
    }
    return this._project;
  }

  private getProjectConfigIf<T, P extends ScopedProject>(
    scopePredicate: (p: ScopedProject) => p is P,
    selector: (p: P) => T | undefined,
  ): T | undefined {
    const proj = this.project;
    return scopePredicate(proj) ? selector(proj) : undefined;
  }

  getAlarmDeviceConfig(id: string): AlarmDeviceConfiguration | undefined {
    return this.getProjectConfigIf(isFgdsProject, (p) => p.getAlarmDeviceById(id));
  }

  getTransmitterConfig(id: string): TransmitterConfiguration | undefined {
    return this.getProjectConfigIf(isFgdsProject, (p) => p.getTransmitterById(id));
  }

  getFlameDetectorConfig(id: string): FlameDetectorConfiguration | undefined {
    return this.getProjectConfigIf(isFgdsProject, (p) => p.getFlameDetectorById(id));
  }

  getGasWarningCenterConfig(id: string): GasWarningCenterConfiguration | undefined {
    return this.getProjectConfigIf(isFgdsProject, (p) => p.getGasWarningCenterById(id));
  }

  getSignalElementConfig(id: string): SignalElementConfiguration | undefined {
    return this.getProjectConfigIf(isFgdsProject, (p) => p.getSignalElementById(id));
  }

  getPlasticSignConfig(id: string): PlasticSignConfiguration | undefined {
    return this.getProjectConfigIf(isFgdsProject, (p) => p.getPlasticSignById(id));
  }

  getPatientMonitorConfig(id: string): PatientMonitorConfiguration | undefined {
    return this.getProjectConfigIf(isMonitoringProject, (p) => p.getPatientMonitorById(id));
  }

  getCentralStationConfig(id: string): CentralStationConfiguration | undefined {
    return this.getProjectConfigIf(isMonitoringProject, (p) => p.getCentralStationById(id));
  }

  getTelemetryConfig(id: string): TelemetryConfiguration | undefined {
    return this.getProjectConfigIf(isMonitoringProject, (p) => p.getTelemetryById(id));
  }

  getKvmConfig(id: string): KvmConfiguration | undefined {
    return this.getProjectConfigIf(isMonitoringProject, (p) => p.getKvmById(id));
  }

  getDisplayConfig(id: string): DisplayConfiguration | undefined {
    return this.getProjectConfigIf(isMonitoringProject, (p) => p.getDisplayById(id));
  }

  getUtilityConfig(id: string): UtilityConfiguration | undefined {
    return this.getProjectConfigIf(isMonitoringProject, (p) => p.getUtilityById(id));
  }

  get floorplanState(): FloorplanState {
    return this._floorplanState;
  }

  get images(): FloorplanImage[] {
    return this._images;
  }

  get alarmDevices(): FloorplanAlarmDevice[] {
    return this._alarmDevices;
  }

  get alarmDevicePlaceholders(): FloorplanSimplePlaceholder[] {
    return this._alarmDevicePlaceholders;
  }

  get transmitters(): FloorplanTransmitter[] {
    return this._transmitters;
  }

  get transmitterPlaceholders(): FloorplanTransmitterPlaceholder[] {
    return this._transmitterPlaceholders;
  }

  get flameDetectors(): FloorplanFlameDetector[] {
    return this._flameDetectors;
  }

  get flameDetectorPlaceholders(): FloorplanFlameDetectorPlaceholder[] {
    return this._flameDetectorPlaceholders;
  }

  get signalElements(): FloorplanSignalElement[] {
    return this._signalElements;
  }

  get signalElementPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._signalElementPlaceholders;
  }

  get plasticSigns(): FloorplanPlasticSign[] {
    return this._plasticSigns;
  }

  get plasticSignPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._plasticSignPlaceholders;
  }

  get gasWarningCenters(): FloorplanGasWarningCenter[] {
    return this._gasWarningCenters;
  }

  get gasWarningCenterPlaceholders(): FloorplanGasWarningCenterPlaceholder[] {
    return this._gasWarningCenterPlaceholders;
  }

  get floorplanTexts(): FloorplanText[] {
    return this._floorplanTexts;
  }

  get floorplanFreeTexts(): FloorplanFreeText[] {
    return this._floorplanFreeTexts;
  }

  get exZones(): ExZone[] {
    return this._exZones;
  }

  get dustExZones(): DustExZone[] {
    return this._dustExZones;
  }

  get wildcardZones(): WildcardZone[] {
    return this._wildcardZones;
  }

  get dangerAreas(): DangerArea[] {
    return this._dangerAreas;
  }

  get measurementLines(): MeasurementLine[] {
    return this._measurementLines;
  }

  get referenceMeasure(): ReferenceMeasure | undefined {
    return this._referenceMeasure;
  }

  get airPaths(): AirPath[] {
    return this._airPaths;
  }

  get pipelines(): Pipeline[] {
    return this._pipelines;
  }

  get cableRoutes(): CableRoute[] {
    return this._cableRoutes;
  }

  get protectiveTubes(): ProtectiveTube[] {
    return this._protectiveTubes;
  }

  get wires(): Wire[] {
    return this._wires;
  }

  get patientMonitors(): FloorplanPatientMonitor[] {
    return this._patientMonitors;
  }

  get patientMonitorPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._patientMonitorPlaceholders;
  }

  get centralStations(): FloorplanCentralStation[] {
    return this._centralStations;
  }

  get centralStationPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._centralStationPlaceholders;
  }

  get telemetries(): FloorplanTelemetry[] {
    return this._telemetries;
  }

  get telemetryPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._telemetryPlaceholders;
  }

  get kvms(): FloorplanKvm[] {
    return this._kvms;
  }

  get kvmPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._kvmPlaceholders;
  }

  get displays(): FloorplanDisplay[] {
    return this._displays;
  }

  get displayPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._displayPlaceholders;
  }

  get utilities(): FloorplanUtility[] {
    return this._utilities;
  }

  get utilityPlaceholders(): FloorplanSimplePlaceholder[] {
    return this._utilityPlaceholders;
  }

  get isolationRooms(): IsolationRoom[] {
    return this._isolationRooms;
  }

  get itRooms(): ItRoom[] {
    return this._itRooms;
  }

  get operatingTheatres(): OperatingTheatre[] {
    return this._operatingTheatres;
  }

  get procedureRooms(): ProcedureRoom[] {
    return this._procedureRooms;
  }

  get standardRooms(): StandardRoom[] {
    return this._standardRooms;
  }

  registerDuplicate<T extends FloorplanItem>(
    duplicate: T,
    targetCollection: FloorplanItem[],
    addedEventType: FloorplanEventType,
    refreshFloorplanLockState: () => void,
    refreshPositionIds?: () => void,
    afterDuplicateAdded?: (duplicate: T) => void,
  ): T {
    targetCollection.push(duplicate);
    this.publishUpdate(addedEventType, duplicate);

    refreshFloorplanLockState();
    refreshPositionIds?.();

    afterDuplicateAdded?.(duplicate);

    return duplicate;
  }

  addImage(image: ProjectImageMetadata, x: number, y: number, size: number = 60): FloorplanImage {
    if (!this._project) {
      throw Error("Project must be set to add image");
    }

    const img = FloorplanImage.create(this, image, x, y, size);
    img.setItemTextVisibility();
    this._images.push(img);
    this.publishUpdate(FloorplanEventType.IMAGE_ADDED, img);
    this.floorplanState.refreshImagesLock();
    this.project.setImagePositionIds();
    return img;
  }

  addAlarmDevice(alarmDevice: AlarmDeviceConfiguration, x: number, y: number, size: number): FloorplanAlarmDevice {
    if (!this._project) {
      throw Error("Project must be set to add alarm device");
    }
    const floorplanAlarmDevice = FloorplanAlarmDevice.create(this, alarmDevice, x, y, size);
    floorplanAlarmDevice.setItemTextVisibility();
    this._alarmDevices.push(floorplanAlarmDevice);
    this.publishUpdate(FloorplanEventType.ALARM_DEVICE_ADDED, floorplanAlarmDevice);
    this.floorplanState.refreshAlarmDevicesLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanAlarmDevice.id, FgdsProductType.ALARMDEVICE);
    return floorplanAlarmDevice;
  }

  addAlarmDevicePlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "alarmDevice");
    floorplanPlaceholder.setItemTextVisibility();
    this._alarmDevicePlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.ALARM_DEVICE_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshAlarmDevicesLock();
    this.project.setAlarmDevicePlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, FgdsProductType.ALARMDEVICE);
    return floorplanPlaceholder;
  }

  addTransmitter(
    transmitter: TransmitterConfiguration,
    x: number,
    y: number,
    size: number,
    monitoringAreaRadius: number,
    remoteSensor?: FloorplanRemoteSensor,
    bmk?: string,
    sector?: string,
    installationHeight?: string,
    exProtection?: boolean,
  ): FloorplanTransmitter {
    if (!this._project) {
      throw Error("Project must be set to add transmitter");
    }
    const floorplanTransmitter = FloorplanTransmitter.create(
      this,
      transmitter,
      x,
      y,
      size,
      monitoringAreaRadius,
      remoteSensor,
      bmk,
      sector,
      installationHeight,
      exProtection,
    );
    return this.registerTransmitter(floorplanTransmitter);
  }

  addTransmitterPlaceholder(x: number, y: number, size: number, monitoringAreaRadius: number): FloorplanTransmitterPlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }

    const floorplanPlaceholder = FloorplanTransmitterPlaceholder.create(this, x, y, size, monitoringAreaRadius);
    floorplanPlaceholder.setItemTextVisibility();
    this._transmitterPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.TRANSMITTER_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshTransmittersLock();
    this.project.setTransmitterPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, FgdsProductType.TRANSMITTER);
    return floorplanPlaceholder;
  }

  addFlameDetector(
    flameDetector: FlameDetectorConfiguration,
    x: number,
    y: number,
    size: number,
    coverageAreaLength: number,
    sensitivity?: string,
    mountingHeight?: string,
  ): FloorplanFlameDetector {
    if (!this._project) {
      throw Error("Project must be set to add flameDetector");
    }
    const floorplanFlameDetector = FloorplanFlameDetector.create(
      this,
      flameDetector,
      x,
      y,
      size,
      coverageAreaLength,
      sensitivity,
      mountingHeight,
    );
    return this.registerFlameDetector(floorplanFlameDetector);
  }

  addFlameDetectorPlaceholder(x: number, y: number, size: number, coverageAreaLength: number): FloorplanFlameDetectorPlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }

    const floorplanPlaceholder = FloorplanFlameDetectorPlaceholder.create(this, x, y, size, coverageAreaLength);
    floorplanPlaceholder.setItemTextVisibility();
    this._flameDetectorPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.FLAME_DETECTOR_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshFlameDetectorsLock();
    this.project.setFlameDetectorPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, FgdsProductType.FLAME_DETECTOR);
    return floorplanPlaceholder;
  }

  addGasWarningCenter(
    gasWarningCenter: GasWarningCenterConfiguration,
    x: number,
    y: number,
    size: number,
    cabinet?: FloorplanGasWarningCenterCabinet,
  ): FloorplanGasWarningCenter {
    if (!this._project) {
      throw Error("Project must be set to add gas warning center");
    }
    const floorplanGasWarningCenter = FloorplanGasWarningCenter.create(this, gasWarningCenter, x, y, size, cabinet);
    floorplanGasWarningCenter.setItemTextVisibility();
    this._gasWarningCenters.push(floorplanGasWarningCenter);
    this.publishUpdate(FloorplanEventType.GAS_WARNING_CENTER_ADDED, floorplanGasWarningCenter);
    this.floorplanState.refreshGasWarningCentersLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanGasWarningCenter.id, FgdsProductType.GASWARNINGCENTER);
    return floorplanGasWarningCenter;
  }

  addGasWarningCenterPlaceholder(x: number, y: number, size: number): FloorplanGasWarningCenterPlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanGasWarningCenterPlaceholder.create(this, x, y, size);
    floorplanPlaceholder.setItemTextVisibility();
    this._gasWarningCenterPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.GAS_WARNING_CENTER_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshGasWarningCentersLock();
    this.project.setGasWarningCenterPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, FgdsProductType.GASWARNINGCENTER);
    return floorplanPlaceholder;
  }

  addSignalElement(signalElement: SignalElementConfiguration, x: number, y: number, size: number): FloorplanSignalElement {
    if (!this._project) {
      throw Error("Project must be set to add signal element");
    }
    const floorplanSignalElement = FloorplanSignalElement.create(this, signalElement, x, y, size);
    floorplanSignalElement.setItemTextVisibility();
    this._signalElements.push(floorplanSignalElement);
    this.publishUpdate(FloorplanEventType.SIGNAL_ELEMENT_ADDED, floorplanSignalElement);
    this.floorplanState.refreshSignalElementsLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanSignalElement.id, FgdsProductType.SIGNALELEMENT);
    return floorplanSignalElement;
  }

  addSignalElementPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "signalElement");
    floorplanPlaceholder.setItemTextVisibility();
    this._signalElementPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.SIGNAL_ELEMENT_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshSignalElementsLock();
    this.project.setSignalElementPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, FgdsProductType.SIGNALELEMENT);
    return floorplanPlaceholder;
  }

  addPlasticSign(plasticSign: PlasticSignConfiguration, x: number, y: number, size: number): FloorplanPlasticSign {
    if (!this._project) {
      throw Error("Project must be set to add plastic sign");
    }
    const floorplanPlasticSign = FloorplanPlasticSign.create(this, plasticSign, x, y, size);
    floorplanPlasticSign.setItemTextVisibility();
    this._plasticSigns.push(floorplanPlasticSign);
    this.publishUpdate(FloorplanEventType.PLASTIC_SIGN_ADDED, floorplanPlasticSign);
    this.floorplanState.refreshPlasticSignsLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanPlasticSign.id, FgdsProductType.PLASTICSIGN);
    return floorplanPlasticSign;
  }

  addPlasticSignPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "plasticSign");
    floorplanPlaceholder.setItemTextVisibility();
    this._plasticSignPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.PLASTIC_SIGN_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshPlasticSignsLock();
    this.project.setPlasticSignPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, FgdsProductType.PLASTICSIGN);
    return floorplanPlaceholder;
  }

  addFloorplanText(x: number, y: number, size: number): FloorplanText {
    const floorplanText = FloorplanText.create(this, x, y, size);
    floorplanText.setItemTextVisibility();
    this._floorplanTexts.push(floorplanText);
    this.publishUpdate(FloorplanEventType.TEXT_ADDED, floorplanText);
    this.floorplanState.refreshFloorplanTextsLock();
    this.project.setFloorplanTextPositionIds();
    return floorplanText;
  }

  addFloorplanFreeText(x: number, y: number, content: string = ""): FloorplanFreeText {
    const floorplanFreeText = FloorplanFreeText.create(this, x, y, content);
    floorplanFreeText.setItemTextVisibility();
    this._floorplanFreeTexts.push(floorplanFreeText);
    this.project.setFloorplanFreeTextPositionIds();
    this.publishUpdate(FloorplanEventType.FREE_TEXT_ADDED, floorplanFreeText);
    this.floorplanState.refreshFloorplanFreeTextsLock();
    return floorplanFreeText;
  }

  addExZone(
    exZoneType: ExZoneType,
    x: number,
    y: number,
    width: number = 100,
    height: number = 150,
    rotation: number = 0,
  ): ExZone {
    const exZone = ExZone.create(this, exZoneType, x, y, width, height, rotation);
    exZone.setItemTextVisibility();
    this._exZones.push(exZone);
    this.publishUpdate(FloorplanEventType.EX_ZONE_ADDED, exZone);
    this.floorplanState.refreshExZonesLock();
    this.project.setExZonePositionIds();
    return exZone;
  }

  addDustExZone(
    dustExZoneType: DustExZoneType,
    x: number,
    y: number,
    width: number = 100,
    height: number = 150,
    rotation: number = 0,
  ): DustExZone {
    const dustExZone = DustExZone.create(this, dustExZoneType, x, y, width, height, rotation);
    dustExZone.setItemTextVisibility();
    this._dustExZones.push(dustExZone);
    this.publishUpdate(FloorplanEventType.DUST_EX_ZONE_ADDED, dustExZone);
    this.floorplanState.refreshDustExZonesLock();
    this.project.setDustExZonePositionIds();
    return dustExZone;
  }

  addWildcardZone(
    type: WildcardZoneType,
    x: number,
    y: number,
    width: number = 100,
    height: number = 150,
    rotation: number = 0,
  ): WildcardZone {
    const zone = WildcardZone.create(this, type, x, y, width, height, rotation);
    zone.setItemTextVisibility();
    this._wildcardZones.push(zone);
    this.publishUpdate(FloorplanEventType.WILDCARD_ZONE_ADDED, zone);
    this.floorplanState.refreshWildcardZonesLock();
    this.project.setWildcardZonePositionIds();
    return zone;
  }

  addDangerArea(x: number, y: number, width: number = 100, height: number = 150, rotation: number = 0): DangerArea {
    if (!this._project) {
      throw Error("Project must be set to add danger area");
    }
    const dangerArea = DangerArea.create(this, x, y, width, height, rotation);
    dangerArea.setItemTextVisibility();
    this._dangerAreas.push(dangerArea);
    this.publishUpdate(FloorplanEventType.DANGER_AREA_ADDED, dangerArea);
    this.floorplanState.refreshDangerAreasLock();
    this.project.setDangerAreaPositionIds();
    return dangerArea;
  }

  addMeasurementLine(
    x: number,
    y: number,
    text: string,
    width: number = 150,
    height: number = 50,
    rotation: number = 0,
  ): MeasurementLine {
    if (!this._project) {
      throw Error("Project must be set to add measurement line");
    }
    const measurementLine = MeasurementLine.create(this, x, y, width, height, rotation, text);
    measurementLine.setItemTextVisibility();
    this._measurementLines.push(measurementLine);
    this.publishUpdate(FloorplanEventType.MEASUREMENT_LINE_ADDED, measurementLine);
    this.floorplanState.refreshMeasurementLinesLock();
    this.project.setMeasurementLinePositionIds();
    return measurementLine;
  }

  addReferenceMeasure(
    x: number,
    y: number,
    scale: number = 1,
    width: number = 150,
    height: number = 50,
    rotation: number = 0,
  ): ReferenceMeasure {
    const referenceMeasure = ReferenceMeasure.create(this, x, y, width, height, rotation, scale);
    referenceMeasure.setItemTextVisibility();
    this._referenceMeasure = referenceMeasure;
    this.publishUpdate(FloorplanEventType.REFERENCE_MEASURE_ADDED, referenceMeasure);
    this.floorplanState.refreshReferenceMeasureLock();
    this.project.setReferenceMeasurePositionIds();
    return referenceMeasure;
  }

  addAirPath(
    direction: AirPathDirection,
    x: number,
    y: number,
    length: number = 150,
    strokeWidth: number = 20,
    rotation: number = 0,
  ): AirPath {
    const airPath = AirPath.create(this, direction, x, y, length, strokeWidth, rotation);
    airPath.setItemTextVisibility();
    this._airPaths.push(airPath);
    this.publishUpdate(FloorplanEventType.AIR_PATH_ADDED, airPath);
    this.floorplanState.refreshAirPathsLock();
    this.project.setAirPathPositionIds();
    return airPath;
  }

  addPipeline(
    x: number,
    y: number,
    width: number = 150,
    height: number = 20,
    diameter?: string,
    medium?: string,
    temperature?: string,
    material?: string,
    rotation: number = 0,
    name?: string,
  ): Pipeline {
    const pipeline = Pipeline.create(this, x, y, width, height, diameter, medium, temperature, material, rotation, name);
    pipeline.setItemTextVisibility();
    this._pipelines.push(pipeline);
    this.publishUpdate(FloorplanEventType.PIPELINE_ADDED, pipeline);
    this.floorplanState.refreshPipelinesLock();
    this.project.setPipelinePositionIds();
    return pipeline;
  }

  addCableRoute(
    x: number,
    y: number,
    width: number = 150,
    height: number = 30,
    constructionKit?: boolean,
    scalingLength?: string,
    rotation: number = 0,
    name?: string,
    displayName?: string,
  ): CableRoute {
    const cableRoute = CableRoute.create(this, x, y, width, height, constructionKit, scalingLength, rotation, name, displayName);
    cableRoute.setItemTextVisibility();
    this._cableRoutes.push(cableRoute);
    this.publishUpdate(FloorplanEventType.CABLE_ROUTE_ADDED, cableRoute);
    this.floorplanState.refreshCableRoutesLock();
    this.project.setCableRoutePositionIds();
    return cableRoute;
  }

  addProtectiveTube(
    x: number,
    y: number,
    width: number = 150,
    height: number = 20,
    material?: string,
    rotation: number = 0,
    name: string = "",
  ): ProtectiveTube {
    const protectiveTube = ProtectiveTube.create(this, x, y, width, height, material, rotation, name);
    protectiveTube.setItemTextVisibility();
    this._protectiveTubes.push(protectiveTube);
    this.publishUpdate(FloorplanEventType.PROTECTIVE_TUBE_ADDED, protectiveTube);
    this.floorplanState.refreshProtectiveTubesLock();
    this.project.setProtectiveTubePositionIds();
    return protectiveTube;
  }

  addWire(
    x: number,
    y: number,
    width: number = 150,
    height: number = 3,
    type?: string,
    scalingLength?: string,
    rotation: number = 0,
    name?: string,
  ): Wire {
    const wire = Wire.create(this, x, y, width, height, type, scalingLength, rotation, name);
    wire.setItemTextVisibility();
    this._wires.push(wire);
    this.publishUpdate(FloorplanEventType.WIRE_ADDED, wire);
    this.floorplanState.refreshWiresLock();
    this.project.setWirePositionIds();
    return wire;
  }

  addPatientMonitor(
    patientMonitorConfig: PatientMonitorConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanPatientMonitor {
    if (!this._project) {
      throw Error("Project must be set to add patient monitor");
    }
    const floorplanPatientMonitor = FloorplanPatientMonitor.create(this, patientMonitorConfig, x, y, size);
    floorplanPatientMonitor.setItemTextVisibility();
    this._patientMonitors.push(floorplanPatientMonitor);
    this.publishUpdate(FloorplanEventType.PATIENT_MONITOR_ADDED, floorplanPatientMonitor);
    this.floorplanState.refreshPatientMonitorsLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanPatientMonitor.id, MonitoringCategory.PATIENT_MONITORS);
    return floorplanPatientMonitor;
  }

  addPatientMonitorPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "patientMonitor");
    floorplanPlaceholder.setItemTextVisibility();
    this._patientMonitorPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.PATIENT_MONITOR_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshPatientMonitorsLock();
    this.project.setPatientMonitorPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, MonitoringCategory.PATIENT_MONITORS);
    return floorplanPlaceholder;
  }

  addCentralStation(
    centralStationConfig: CentralStationConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanCentralStation {
    if (!this._project) {
      throw Error("Project must be set to add central station");
    }
    const floorplanCentralStation = FloorplanCentralStation.create(this, centralStationConfig, x, y, size);
    floorplanCentralStation.setItemTextVisibility();
    this._centralStations.push(floorplanCentralStation);
    this.publishUpdate(FloorplanEventType.CENTRAL_STATION_ADDED, floorplanCentralStation);
    this.floorplanState.refreshCentralStationsLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanCentralStation.id, MonitoringCategory.CENTRAL_STATIONS);
    return floorplanCentralStation;
  }

  addCentralStationPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "centralStation");
    floorplanPlaceholder.setItemTextVisibility();
    this._centralStationPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.CENTRAL_STATION_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshCentralStationsLock();
    this.project.setCentralStationPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, MonitoringCategory.CENTRAL_STATIONS);
    return floorplanPlaceholder;
  }

  addTelemetry(telemetryConfig: TelemetryConfiguration, x: number, y: number, size: number): FloorplanTelemetry {
    if (!this._project) {
      throw Error("Project must be set to add telemetry");
    }
    const floorplanTelemetry = FloorplanTelemetry.create(this, telemetryConfig, x, y, size);
    floorplanTelemetry.setItemTextVisibility();
    this._telemetries.push(floorplanTelemetry);
    this.publishUpdate(FloorplanEventType.TELEMETRY_ADDED, floorplanTelemetry);
    this.floorplanState.refreshTelemetriesLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanTelemetry.id, MonitoringCategory.TELEMETRY);
    return floorplanTelemetry;
  }

  addTelemetryPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "telemetry");
    floorplanPlaceholder.setItemTextVisibility();
    this._telemetryPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.TELEMETRY_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshTelemetriesLock();
    this.project.setTelemetryPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, MonitoringCategory.TELEMETRY);
    return floorplanPlaceholder;
  }

  addKvm(kvmConfig: KvmConfiguration, x: number, y: number, size: number): FloorplanKvm {
    if (!this._project) {
      throw Error("Project must be set to add kvm");
    }
    const floorplanKvm = FloorplanKvm.create(this, kvmConfig, x, y, size);
    floorplanKvm.setItemTextVisibility();
    floorplanKvm.reconcileReceiversWithConfig();
    this._kvms.push(floorplanKvm);
    this.publishUpdate(FloorplanEventType.KVM_ADDED, floorplanKvm);
    this.floorplanState.refreshKvmsLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanKvm.id, MonitoringCategory.KVMS);
    return floorplanKvm;
  }

  addKvmPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "kvm");
    floorplanPlaceholder.setItemTextVisibility();
    this._kvmPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.KVM_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshKvmsLock();
    this.project.setKvmPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, MonitoringCategory.KVMS);
    return floorplanPlaceholder;
  }

  addDisplay(displayConfig: DisplayConfiguration, x: number, y: number, size: number): FloorplanDisplay {
    if (!this._project) {
      throw Error("Project must be set to add display");
    }
    const floorplanDisplay = FloorplanDisplay.create(this, displayConfig, x, y, size);
    floorplanDisplay.setItemTextVisibility();
    this._displays.push(floorplanDisplay);
    this.publishUpdate(FloorplanEventType.DISPLAY_ADDED, floorplanDisplay);
    this.floorplanState.refreshDisplaysLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanDisplay.id, MonitoringCategory.DISPLAYS);
    return floorplanDisplay;
  }

  addDisplayPlaceholder(x: number, y: number, size: number): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, FloorplanPlaceholderType.DISPLAY);
    floorplanPlaceholder.setItemTextVisibility();
    this._displayPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.DISPLAY_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshDisplaysLock();
    this.project.setDisplayPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, MonitoringCategory.DISPLAYS);
    return floorplanPlaceholder;
  }

  duplicateDisplayPlaceholder(
    x: number,
    y: number,
    size: number,
    displayName?: string,
    products?: PlaceholderProduct[],
    notes?: string,
  ): FloorplanSimplePlaceholder {
    if (!this._project) {
      throw Error("Project must be set to duplicate placeholder");
    }
    const floorplanDisplayPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, FloorplanPlaceholderType.DISPLAY);
    if (displayName) floorplanDisplayPlaceholder.displayName = displayName;
    if (products)
      floorplanDisplayPlaceholder.products = products.map(
        (product) => new PlaceholderProduct(product.name, product.id, product.positionId, product.price),
      );
    if (notes) floorplanDisplayPlaceholder.notes = notes;

    this._displayPlaceholders.push(floorplanDisplayPlaceholder);
    this.publishUpdate(FloorplanEventType.DISPLAY_PLACEHOLDER_ADDED, floorplanDisplayPlaceholder);
    this.floorplanState.refreshDisplaysLock();

    if (products) {
      this.project.setPositionNumbers();
    } else {
      this.project.setDisplayPlaceholderPositionIds();
    }
    this.registerProductItemReference(floorplanDisplayPlaceholder.id, MonitoringCategory.DISPLAYS);
    return floorplanDisplayPlaceholder;
  }

  addUtility(utilityConfig: UtilityConfiguration, x: number, y: number, size: number): FloorplanUtility {
    if (!this._project) {
      throw Error("Project must be set to add utility");
    }
    const floorplanUtility = FloorplanUtility.create(this, utilityConfig, x, y, size);
    floorplanUtility.setItemTextVisibility();
    this._utilities.push(floorplanUtility);
    this.publishUpdate(FloorplanEventType.UTILITY_ADDED, floorplanUtility);
    this.floorplanState.refreshUtilitiesLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanUtility.id, MonitoringCategory.UTILITY);
    return floorplanUtility;
  }

  addUtilityPlaceholder(x: number, y: number, size: number): FloorplanPlaceholder {
    if (!this._project) {
      throw Error("Project must be set to add placeholder");
    }
    const floorplanPlaceholder = FloorplanSimplePlaceholder.create(this, x, y, size, "utility");
    floorplanPlaceholder.setItemTextVisibility();
    this._utilityPlaceholders.push(floorplanPlaceholder);
    this.publishUpdate(FloorplanEventType.UTILITY_PLACEHOLDER_ADDED, floorplanPlaceholder);
    this.floorplanState.refreshUtilitiesLock();
    this.project.setUtilityPlaceholderPositionIds();
    this.registerProductItemReference(floorplanPlaceholder.id, MonitoringCategory.UTILITY);
    return floorplanPlaceholder;
  }

  addIsolationRoom(x: number, y: number, width: number = 100, height: number = 150, rotation: number = 0): IsolationRoom {
    if (!this._project) {
      throw Error("Project must be set to add isolation room");
    }
    const isolationRoom = IsolationRoom.create(this, x, y, width, height, rotation);
    this._isolationRooms.push(isolationRoom);
    this.publishUpdate(FloorplanEventType.ISOLATION_ROOM_ADDED, isolationRoom);
    this.floorplanState.refreshIsolationRoomsLock();
    this.project.setIsolationRoomPositionIds();
    return isolationRoom;
  }

  addItRoom(x: number, y: number, width: number = 100, height: number = 150, rotation: number = 0): ItRoom {
    if (!this._project) {
      throw Error("Project must be set to add it room");
    }
    const itRoom = ItRoom.create(this, x, y, width, height, rotation);
    this._itRooms.push(itRoom);
    this.publishUpdate(FloorplanEventType.IT_ROOM_ADDED, itRoom);
    this.floorplanState.refreshItRoomsLock();
    this.project.setItRoomPositionIds();
    return itRoom;
  }

  addOperatingTheatre(x: number, y: number, width: number = 100, height: number = 150, rotation: number = 0): OperatingTheatre {
    if (!this._project) {
      throw Error("Project must be set to add operatingTheatre");
    }
    const operatingTheatre = OperatingTheatre.create(this, x, y, width, height, rotation);
    this._operatingTheatres.push(operatingTheatre);
    this.publishUpdate(FloorplanEventType.OPERATING_THEATRE_ADDED, operatingTheatre);
    this.floorplanState.refreshOperatingTheatresLock();
    this.project.setOperatingTheatrePositionIds();
    return operatingTheatre;
  }

  addProcedureRoom(x: number, y: number, width: number = 100, height: number = 150, rotation: number = 0): ProcedureRoom {
    if (!this._project) {
      throw Error("Project must be set to add procedure Room");
    }
    const procedureRoom = ProcedureRoom.create(this, x, y, width, height, rotation);
    this._procedureRooms.push(procedureRoom);
    this.publishUpdate(FloorplanEventType.PROCEDURE_ROOM_ADDED, procedureRoom);
    this.floorplanState.refreshProcedureRoomsLock();
    this.project.setProcedureRoomPositionIds();
    return procedureRoom;
  }

  addStandardRoom(x: number, y: number, width: number = 100, height: number = 150, rotation: number = 0): StandardRoom {
    if (!this._project) {
      throw Error("Project must be set to add standardRoom");
    }
    const standardRoom = StandardRoom.create(this, x, y, width, height, rotation);
    this._standardRooms.push(standardRoom);
    this.publishUpdate(FloorplanEventType.STANDARD_ROOM_ADDED, standardRoom);
    this.floorplanState.refreshStandardRoomsLock();
    this.project.setStandardRoomPositionIds();
    return standardRoom;
  }

  countPlacedAlarmDevices(alarmDevice: AlarmDeviceConfiguration): number {
    return this.alarmDevices.filter((floorplanAlarmDevice) => floorplanAlarmDevice.config.id === alarmDevice?.id).length;
  }

  countPlacedTransmitters(transmitter: TransmitterConfiguration): number {
    return this.transmitters.filter((floorplanTransmitter) => floorplanTransmitter.config.id === transmitter?.id).length;
  }

  applyTransmitterConfigDefaultDetectionRange(transmitter: TransmitterConfiguration) {
    this.transmitters.forEach((floorplanTransmitter) => {
      if (floorplanTransmitter.config.id === transmitter.id) {
        floorplanTransmitter.applyDefaultDetectionRange();
      }
    });
  }

  countPlacedFlameDetectors(flameDetector: FlameDetectorConfiguration): number {
    return this.flameDetectors.filter((floorplanFlameDetector) => floorplanFlameDetector.config.id === flameDetector?.id).length;
  }

  applyFlameDetectorConfigDefaultDetectionLength(flameDetector: FlameDetectorConfiguration) {
    this.flameDetectors.forEach((floorplanFlameDetector) => {
      if (floorplanFlameDetector.config.id === flameDetector.id) {
        floorplanFlameDetector.applyDefaultDetectionLength();
      }
    });
  }

  countPlacedGasWarningCenters(gasWarningCenter: GasWarningCenterConfiguration): number {
    return this._gasWarningCenters.filter(
      (floorplanGasWarningCenter) => floorplanGasWarningCenter.config.id === gasWarningCenter?.id,
    ).length;
  }

  countPlacedSignalElements(signalElement: SignalElementConfiguration): number {
    return this.signalElements.filter((floorplanSignalElement) => floorplanSignalElement.config.id === signalElement?.id).length;
  }

  countPlacedPlasticSigns(plasticSign: PlasticSignConfiguration): number {
    return this.plasticSigns.filter((floorplanPlasticSign) => floorplanPlasticSign.config.id === plasticSign?.id).length;
  }

  countPlacedPatientMonitors(patientMonitor: PatientMonitorConfiguration): number {
    return this._patientMonitors.filter((floorplanPatientMonitor) => floorplanPatientMonitor.config.id === patientMonitor?.id)
      .length;
  }

  countPlacedCentralStations(centralStation: CentralStationConfiguration): number {
    return this._centralStations.filter((floorplanCentralStation) => floorplanCentralStation.config.id === centralStation?.id)
      .length;
  }

  countPlacedTelemetries(telemetry: TelemetryConfiguration): number {
    return this._telemetries.filter((floorplanTelemetry) => floorplanTelemetry.config.id === telemetry?.id).length;
  }

  countPlacedKvms(kvm: KvmConfiguration): number {
    return this._kvms.filter((floorplanKvm) => floorplanKvm.config.id === kvm?.id).length;
  }

  countPlacedDisplays(display: DisplayConfiguration): number {
    return this._displays.filter((floorplanDisplay) => floorplanDisplay.config.id === display?.id).length;
  }

  countPlacedUtilities(utility: UtilityConfiguration): number {
    return this._utilities.filter((floorplanUtility) => floorplanUtility.config.id === utility?.id).length;
  }

  deleteImagesByProjectImage(projectImage: ProjectImageMetadata) {
    this._images
      .filter((image) => {
        return image.projectImageId === projectImage.id;
      })
      .forEach((floorplanImage) => this.deleteImage(floorplanImage));
  }

  deleteImage(floorplanImage: FloorplanImage) {
    this.removeFromArray(this._images, floorplanImage);
    this.publishUpdate(FloorplanEventType.IMAGE_DELETED, floorplanImage);
    this.project.setImagePositionIds();
  }

  private registerProductItemReference(itemId: string, kind: FgdsProductType | MonitoringCategory): void {
    this.project.addProductItemReference(new FloorplanItemReference(this.id, itemId, kind));
  }

  private removeItemReferenceFromAllGroups(itemId: string) {
    const affectedGroups = this.project.groups.filter((group) =>
      group.references.some((ref) => ref.floorplanId === this.id && ref.floorplanProductItemId === itemId),
    );
    affectedGroups.forEach((group) => group.removeReference(this.id, itemId));
    this.project.removeProductItemReference(this.id, itemId);
    affectedGroups.forEach((group) => this.project.removeGroupIfEmpty(group));
  }

  deleteTransmitter(transmitter: FloorplanTransmitter) {
    this.removeFromArray(this._transmitters, transmitter);
    this.removeItemReferenceFromAllGroups(transmitter.id);
    this.publishUpdate(FloorplanEventType.TRANSMITTER_DELETED, transmitter);
    this.project.setAllPositionIds();
  }

  deleteTransmitterPlaceholder(transmitterPlaceholder: FloorplanTransmitterPlaceholder) {
    this.removeFromArray(this._transmitterPlaceholders, transmitterPlaceholder);
    this.removeItemReferenceFromAllGroups(transmitterPlaceholder.id);
    this.publishUpdate(FloorplanEventType.TRANSMITTER_PLACEHOLDER_DELETED, transmitterPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteFlameDetector(flameDetector: FloorplanFlameDetector) {
    this.removeFromArray(this._flameDetectors, flameDetector);
    this.removeItemReferenceFromAllGroups(flameDetector.id);
    this.publishUpdate(FloorplanEventType.FLAME_DETECTOR_DELETED, flameDetector);
    this.project.setAllPositionIds();
  }

  deleteFlameDetectorPlaceholder(flameDetectorPlaceholder: FloorplanFlameDetectorPlaceholder) {
    this.removeFromArray(this._flameDetectorPlaceholders, flameDetectorPlaceholder);
    this.removeItemReferenceFromAllGroups(flameDetectorPlaceholder.id);
    this.publishUpdate(FloorplanEventType.FLAME_DETECTOR_PLACEHOLDER_DELETED, flameDetectorPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteGasWarningCenter(gasWarningCenter: FloorplanGasWarningCenter) {
    this.removeFromArray(this._gasWarningCenters, gasWarningCenter);
    this.removeItemReferenceFromAllGroups(gasWarningCenter.id);
    this.publishUpdate(FloorplanEventType.GAS_WARNING_CENTER_DELETED, gasWarningCenter);
    this.project.setAllPositionIds();
  }

  deleteGasWarningCenterPlaceholder(gasWarningCenterPlaceholder: FloorplanGasWarningCenterPlaceholder) {
    this.removeFromArray(this._gasWarningCenterPlaceholders, gasWarningCenterPlaceholder);
    this.removeItemReferenceFromAllGroups(gasWarningCenterPlaceholder.id);
    this.publishUpdate(FloorplanEventType.GAS_WARNING_CENTER_PLACEHOLDER_DELETED, gasWarningCenterPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteAlarmDevice(alarmDevice: FloorplanAlarmDevice) {
    this.removeFromArray(this._alarmDevices, alarmDevice);
    this.removeItemReferenceFromAllGroups(alarmDevice.id);
    this.publishUpdate(FloorplanEventType.ALARM_DEVICE_DELETED, alarmDevice);
    this.project.setAllPositionIds();
  }

  deleteAlarmDevicePlaceholder(alarmDevicePlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._alarmDevicePlaceholders, alarmDevicePlaceholder);
    this.removeItemReferenceFromAllGroups(alarmDevicePlaceholder.id);
    this.publishUpdate(FloorplanEventType.ALARM_DEVICE_PLACEHOLDER_DELETED, alarmDevicePlaceholder);
    this.project.setAllPositionIds();
  }

  deleteSignalElement(signalElement: FloorplanSignalElement) {
    this.removeFromArray(this._signalElements, signalElement);
    this.removeItemReferenceFromAllGroups(signalElement.id);
    this.publishUpdate(FloorplanEventType.SIGNAL_ELEMENT_DELETED, signalElement);
    this.project.setAllPositionIds();
  }

  deleteSignalElementPlaceholder(signalElementPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._signalElementPlaceholders, signalElementPlaceholder);
    this.removeItemReferenceFromAllGroups(signalElementPlaceholder.id);
    this.publishUpdate(FloorplanEventType.SIGNAL_ELEMENT_PLACEHOLDER_DELETED, signalElementPlaceholder);
    this.project.setAllPositionIds();
  }

  deletePlasticSign(plasticSign: FloorplanPlasticSign) {
    this.removeFromArray(this._plasticSigns, plasticSign);
    this.removeItemReferenceFromAllGroups(plasticSign.id);
    this.publishUpdate(FloorplanEventType.PLASTIC_SIGN_DELETED, plasticSign);
    this.project.setAllPositionIds();
  }

  deletePlasticSignPlaceholder(plasticSignPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._plasticSignPlaceholders, plasticSignPlaceholder);
    this.removeItemReferenceFromAllGroups(plasticSignPlaceholder.id);
    this.publishUpdate(FloorplanEventType.PLASTIC_SIGN_PLACEHOLDER_DELETED, plasticSignPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteFloorplanText(floorplanText: FloorplanText) {
    this.removeFromArray(this._floorplanTexts, floorplanText);
    this.publishUpdate(FloorplanEventType.TEXT_DELETED, floorplanText);
    this.project.setFloorplanTextPositionIds();
  }

  deleteFloorplanFreeText(floorplanFreeText: FloorplanFreeText) {
    this.removeFromArray(this._floorplanFreeTexts, floorplanFreeText);
    this.publishUpdate(FloorplanEventType.FREE_TEXT_DELETED, floorplanFreeText);
    this.floorplanState.refreshFloorplanTextsLock();
    this.project.setFloorplanFreeTextPositionIds();
  }

  deleteExZone(exZone: ExZone) {
    this.removeFromArray(this._exZones, exZone);
    this.publishUpdate(FloorplanEventType.EX_ZONE_DELETED, exZone);
    this.project.setExZonePositionIds();
  }

  deleteDustExZone(dustExZone: DustExZone) {
    this.removeFromArray(this._dustExZones, dustExZone);
    this.publishUpdate(FloorplanEventType.DUST_EX_ZONE_DELETED, dustExZone);
    this.project.setDustExZonePositionIds();
  }

  deleteWildcardZone(zone: WildcardZone) {
    this.removeFromArray(this._wildcardZones, zone);
    this.publishUpdate(FloorplanEventType.WILDCARD_ZONE_DELETED, zone);
    this.project.setWildcardZonePositionIds();
  }

  deleteDangerArea(dangerArea: DangerArea) {
    this.removeFromArray(this._dangerAreas, dangerArea);
    this.publishUpdate(FloorplanEventType.DANGER_AREA_DELETED, dangerArea);
    this.project.setDangerAreaPositionIds();
  }

  deleteMeasurementLine(measurementLine: MeasurementLine) {
    this.removeFromArray(this._measurementLines, measurementLine);
    this.publishUpdate(FloorplanEventType.MEASUREMENT_LINE_DELETED, measurementLine);
    this.project.setMeasurementLinePositionIds();
  }

  deleteReferenceMeasure(referenceMeasure: ReferenceMeasure) {
    this._referenceMeasure = undefined;
    this.publishUpdate(FloorplanEventType.REFERENCE_MEASURE_DELETED, referenceMeasure);
  }

  deleteAirPath(airPath: AirPath) {
    this.removeFromArray(this._airPaths, airPath);
    this.publishUpdate(FloorplanEventType.AIR_PATH_DELETED, airPath);
    this.project.setAirPathPositionIds();
  }

  deletePipeline(pipeline: Pipeline) {
    this.removeFromArray(this._pipelines, pipeline);
    this.publishUpdate(FloorplanEventType.PIPELINE_DELETED, pipeline);
    this.project.setPipelinePositionIds();
  }

  deleteCableRoute(cableRoute: CableRoute) {
    this.removeFromArray(this._cableRoutes, cableRoute);
    this.publishUpdate(FloorplanEventType.CABLE_ROUTE_DELETED, cableRoute);
    this.project.setCableRoutePositionIds();
  }

  deleteProtectiveTube(protectiveTube: ProtectiveTube) {
    this.removeFromArray(this._protectiveTubes, protectiveTube);
    this.publishUpdate(FloorplanEventType.PROTECTIVE_TUBE_DELETED, protectiveTube);
    this.project.setProtectiveTubePositionIds();
  }

  deleteWire(wire: Wire) {
    this.removeFromArray(this._wires, wire);
    this.publishUpdate(FloorplanEventType.WIRE_DELETED, wire);
    this.project.setWirePositionIds();
  }

  deletePatientMonitor(patientMonitor: FloorplanPatientMonitor) {
    this.removeFromArray(this._patientMonitors, patientMonitor);
    this.removeItemReferenceFromAllGroups(patientMonitor.id);
    this.publishUpdate(FloorplanEventType.PATIENT_MONITOR_DELETED, patientMonitor);
    this.project.setAllPositionIds();
  }

  deletePatientMonitorPlaceholder(patientMonitorPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._patientMonitorPlaceholders, patientMonitorPlaceholder);
    this.removeItemReferenceFromAllGroups(patientMonitorPlaceholder.id);
    this.publishUpdate(FloorplanEventType.PATIENT_MONITOR_PLACEHOLDER_DELETED, patientMonitorPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteCentralStation(centralStation: FloorplanCentralStation) {
    this.removeFromArray(this._centralStations, centralStation);
    this.removeItemReferenceFromAllGroups(centralStation.id);
    this.publishUpdate(FloorplanEventType.CENTRAL_STATION_DELETED, centralStation);
    this.project.setAllPositionIds();
  }

  deleteCentralStationPlaceholder(centralStationPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._centralStationPlaceholders, centralStationPlaceholder);
    this.removeItemReferenceFromAllGroups(centralStationPlaceholder.id);
    this.publishUpdate(FloorplanEventType.CENTRAL_STATION_PLACEHOLDER_DELETED, centralStationPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteTelemetry(telemetry: FloorplanTelemetry) {
    this.removeFromArray(this._telemetries, telemetry);
    this.removeItemReferenceFromAllGroups(telemetry.id);
    this.publishUpdate(FloorplanEventType.TELEMETRY_DELETED, telemetry);
    this.project.setAllPositionIds();
  }

  deleteTelemetryPlaceholder(telemetryPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._telemetryPlaceholders, telemetryPlaceholder);
    this.removeItemReferenceFromAllGroups(telemetryPlaceholder.id);
    this.publishUpdate(FloorplanEventType.TELEMETRY_PLACEHOLDER_DELETED, telemetryPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteKvm(kvm: FloorplanKvm) {
    this.removeFromArray(this._kvms, kvm);
    this.removeItemReferenceFromAllGroups(kvm.id);
    this.publishUpdate(FloorplanEventType.KVM_DELETED, kvm);
    this.project.setAllPositionIds();
  }

  deleteKvmPlaceholder(kvmPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._kvmPlaceholders, kvmPlaceholder);
    this.removeItemReferenceFromAllGroups(kvmPlaceholder.id);
    this.publishUpdate(FloorplanEventType.KVM_PLACEHOLDER_DELETED, kvmPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteDisplay(display: FloorplanDisplay) {
    this.removeFromArray(this._displays, display);
    this.removeItemReferenceFromAllGroups(display.id);
    this.publishUpdate(FloorplanEventType.DISPLAY_DELETED, display);
    this.project.setAllPositionIds();
  }

  deleteDisplayPlaceholder(displayPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._displayPlaceholders, displayPlaceholder);
    this.removeItemReferenceFromAllGroups(displayPlaceholder.id);
    this.publishUpdate(FloorplanEventType.DISPLAY_PLACEHOLDER_DELETED, displayPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteUtility(utility: FloorplanUtility) {
    this.removeFromArray(this._utilities, utility);
    this.removeItemReferenceFromAllGroups(utility.id);
    this.publishUpdate(FloorplanEventType.UTILITY_DELETED, utility);
    this.project.setAllPositionIds();
  }

  deleteUtilityPlaceholder(utilityPlaceholder: FloorplanSimplePlaceholder) {
    this.removeFromArray(this._utilityPlaceholders, utilityPlaceholder);
    this.removeItemReferenceFromAllGroups(utilityPlaceholder.id);
    this.publishUpdate(FloorplanEventType.UTILITY_PLACEHOLDER_DELETED, utilityPlaceholder);
    this.project.setAllPositionIds();
  }

  deleteIsolationRoom(isolationRoom: IsolationRoom) {
    this.removeFromArray(this._isolationRooms, isolationRoom);
    this.publishUpdate(FloorplanEventType.ISOLATION_ROOM_DELETED, isolationRoom);
    this.project.setIsolationRoomPositionIds();
  }

  deleteItRoom(itRoom: ItRoom) {
    this.removeFromArray(this._itRooms, itRoom);
    this.publishUpdate(FloorplanEventType.IT_ROOM_DELETED, itRoom);
    this.project.setItRoomPositionIds();
  }

  deleteOperatingTheatre(operatingTheatre: OperatingTheatre) {
    this.removeFromArray(this._operatingTheatres, operatingTheatre);
    this.publishUpdate(FloorplanEventType.OPERATING_THEATRE_DELETED, operatingTheatre);
    this.project.setOperatingTheatrePositionIds();
  }

  deleteProcedureRoom(procedureRoom: ProcedureRoom) {
    this.removeFromArray(this._procedureRooms, procedureRoom);
    this.publishUpdate(FloorplanEventType.PROCEDURE_ROOM_DELETED, procedureRoom);
    this.project.setProcedureRoomPositionIds();
  }

  deleteStandardRoom(standardRoom: StandardRoom) {
    this.removeFromArray(this._standardRooms, standardRoom);
    this.publishUpdate(FloorplanEventType.STANDARD_ROOM_DELETED, standardRoom);
    this.project.setStandardRoomPositionIds();
  }

  deleteAlarmDevicesByConfig(config: AlarmDeviceConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._alarmDevices);
  }

  deleteTransmittersByConfig(config: TransmitterConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._transmitters);
  }

  deleteFlameDetectorsByConfig(config: FlameDetectorConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._flameDetectors);
  }

  deleteGasWarningCentersByConfig(config: GasWarningCenterConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._gasWarningCenters);
  }

  deleteSignalElementsByConfig(config: SignalElementConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._signalElements);
  }

  deletePlasticSignsByConfig(config: PlasticSignConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._plasticSigns);
  }

  deletePatientMonitorsByConfig(config: PatientMonitorConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._patientMonitors);
  }

  deleteCentralStationsByConfig(config: CentralStationConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._centralStations);
  }

  deleteTelemetriesByConfig(config: TelemetryConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._telemetries);
  }

  deleteKvmsByConfig(config: KvmConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._kvms);
  }

  deleteDisplaysByConfig(config: DisplayConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._displays);
  }

  syncKvmReceivers(config: KvmConfiguration) {
    this._kvms
      .filter((kvm) => kvm.config.id === config.id)
      .forEach((kvm) => {
        if (kvm.reconcileReceiversWithConfig()) {
          this.publishUpdate(FloorplanEventType.KVM_RECEIVERS_UPDATED, kvm);
        }
      });
  }

  deleteUtilitiesByConfig(config: UtilityConfiguration) {
    this.deleteFloorplanItemsByConfig(config, this._utilities);
  }

  updateGasWarningCentersPositionIds(config: GasWarningCenterConfiguration) {
    this.updatePositionIds(config, this._gasWarningCenters);
  }

  updateTransmittersPositionIds(config: TransmitterConfiguration) {
    this.updatePositionIds(config, this._transmitters);
  }

  updateFlameDetectorsPositionIds(config: FlameDetectorConfiguration) {
    this.updatePositionIds(config, this._flameDetectors);
  }

  updateAlarmDevicesPositionIds(config: AlarmDeviceConfiguration) {
    this.updatePositionIds(config, this._alarmDevices);
  }

  updateSignalElementsPositionIds(config: SignalElementConfiguration) {
    this.updatePositionIds(config, this._signalElements);
  }

  updatePlasticSignsPositionIds(config: PlasticSignConfiguration) {
    this.updatePositionIds(config, this._plasticSigns);
  }

  updatePatientMonitorsPositionIds(config: PatientMonitorConfiguration) {
    this.updatePositionIds(config, this._patientMonitors);
  }

  updateCentralStationsPositionIds(config: CentralStationConfiguration) {
    this.updatePositionIds(config, this._centralStations);
  }

  updateTelemetriesPositionIds(config: TelemetryConfiguration) {
    this.updatePositionIds(config, this._telemetries);
  }

  updateKvmsPositionIds(config: KvmConfiguration) {
    this.updatePositionIds(config, this._kvms);
  }

  updateDisplaysPositionIds(config: DisplayConfiguration) {
    this.updatePositionIds(config, this._displays);
  }

  updateUtilitiesPositionIds(config: UtilityConfiguration) {
    this.updatePositionIds(config, this._utilities);
  }

  publishUpdate(type: FloorplanEventType, subject: any) {
    if (this._onUpdate) {
      this._onUpdate(this, type, subject);
    }
  }

  getReferenceScale() {
    return this._referenceMeasure && this._referenceMeasure.scale
      ? this._referenceMeasure.width / this._referenceMeasure.scale
      : 1;
  }

  private registerTransmitter(
    floorplanTransmitter: FloorplanTransmitter,
    referenceMonitoringAreaRadius?: number,
  ): FloorplanTransmitter {
    if (referenceMonitoringAreaRadius !== undefined) {
      floorplanTransmitter.updateMonitoringAreaRadius(referenceMonitoringAreaRadius);
    }

    floorplanTransmitter.setItemTextVisibility();
    if (floorplanTransmitter.remoteSensor) floorplanTransmitter.attachRemoteSensor();
    this._transmitters.push(floorplanTransmitter);
    this.publishUpdate(FloorplanEventType.TRANSMITTER_ADDED, floorplanTransmitter);
    this.floorplanState.refreshTransmittersLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanTransmitter.id, FgdsProductType.TRANSMITTER);
    return floorplanTransmitter;
  }

  private registerFlameDetector(
    floorplanFlameDetector: FloorplanFlameDetector,
    referenceCoverageAreaLength?: number,
  ): FloorplanFlameDetector {
    if (referenceCoverageAreaLength !== undefined) {
      floorplanFlameDetector.updateCoverageAreaLength(referenceCoverageAreaLength);
    }

    floorplanFlameDetector.setItemTextVisibility();
    this._flameDetectors.push(floorplanFlameDetector);
    this.publishUpdate(FloorplanEventType.FLAME_DETECTOR_ADDED, floorplanFlameDetector);
    this.floorplanState.refreshFlameDetectorsLock();
    this.project.setPositionNumbers();
    this.registerProductItemReference(floorplanFlameDetector.id, FgdsProductType.FLAME_DETECTOR);
    return floorplanFlameDetector;
  }

  private removeFromArray<T>(array: T[], object: T) {
    const index = array.indexOf(object);
    if (index > -1) {
      array.splice(index, 1);
    }
  }

  private deleteFloorplanItemsByConfig<T extends ProductConfiguration>(
    productConfiguration: T,
    floorplanProductItems: FloorplanProductItem<T>[],
  ) {
    floorplanProductItems
      .filter((floorplanProductItem) => floorplanProductItem.config.id === productConfiguration.id)
      .forEach((floorplanProductItem) => floorplanProductItem.delete());
  }

  private updatePositionIds<T extends ProductConfiguration>(config: T, floorplanItems: FloorplanProductItem<T>[]) {
    let indexCount: number = 0;
    floorplanItems
      .map((floorplanItem, index) => ({ floorplanItem, index }))
      .filter(({ floorplanItem }) => floorplanItem.config === config)
      .sort((a, b) => {
        const suffixDifference =
          this.getNumericPositionIdSuffix(a.floorplanItem.positionId) -
          this.getNumericPositionIdSuffix(b.floorplanItem.positionId);
        return suffixDifference !== 0 ? suffixDifference : a.index - b.index;
      })
      .forEach(({ floorplanItem }) => {
        floorplanItem.positionId = `${config.positionNumber}.${++indexCount}`;
      });
  }

  private getNumericPositionIdSuffix(positionId?: string) {
    const suffix = Number(positionId?.split(".")[1]);
    return Number.isFinite(suffix) ? suffix : Number.MAX_SAFE_INTEGER;
  }
}
